import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Badge, TenantStatusBadge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { IconChevronLeft, IconChevronRight, IconPlus, IconSearch, IconStore } from '@/components/Icons';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState, ErrorState, Skeleton } from '@/components/States';
import { fetchTenants, tenantKeys } from '@/features/tenants/api';
import { OverLimitAlert } from '@/features/plans/OverLimitAlert';
import { formatCount, formatDate } from '@/lib/format';

const PAGE_SIZE = 10;

export function TenantsListPage() {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // Debounce typing before it becomes a query key.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const params = { page, pageSize: PAGE_SIZE, search };
  const { data, isPending, isFetching, isError, error, refetch } = useQuery({
    queryKey: tenantKeys.list(params),
    queryFn: ({ signal }) => fetchTenants(params, signal),
    placeholderData: keepPreviousData,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  return (
    <>
      <PageHeader
        title="Tenants"
        description="Shops registered on the platform, with their branches and sync devices."
        actions={
          <Button
            variant="primary"
            icon={<IconPlus className="h-4 w-4" />}
            onClick={() => navigate('/cp/tenants/new')}
          >
            New tenant
          </Button>
        }
      />

      <OverLimitAlert />

      <div className="card overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3.5 sm:px-5">
          <div className="relative w-full sm:w-72">
            <label htmlFor="tenant-search" className="sr-only">
              Search tenants
            </label>
            <IconSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              id="tenant-search"
              type="search"
              className="input pl-9"
              placeholder="Search by name…"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </div>
          <p className="text-xs text-slate-500" aria-live="polite">
            {isPending ? 'Loading…' : `${formatCount(total)} tenant${total === 1 ? '' : 's'}`}
            {!isPending && isFetching ? ' · refreshing' : ''}
          </p>
        </div>

        {isPending ? (
          <TableSkeleton />
        ) : isError ? (
          <ErrorState error={error} onRetry={() => void refetch()} retrying={isFetching} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<IconStore className="h-5 w-5" />}
            title={search ? `No tenants match “${search}”` : 'No tenants yet'}
            description={
              search
                ? 'Try a different name, or clear the search to see every tenant.'
                : 'Register the first shop, then licence the tills it trades on.'
            }
            action={
              search ? (
                <Button onClick={() => setSearchInput('')}>Clear search</Button>
              ) : (
                <Button variant="primary" icon={<IconPlus className="h-4 w-4" />} onClick={() => navigate('/cp/tenants/new')}>
                  New tenant
                </Button>
              )
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-surface">
                <tr>
                  <th scope="col" className="th">
                    Shop
                  </th>
                  <th scope="col" className="th">
                    Status
                  </th>
                  <th scope="col" className="th">
                    Plan
                  </th>
                  <th scope="col" className="th">
                    Branches
                  </th>
                  <th scope="col" className="th">
                    Devices
                  </th>
                  <th scope="col" className="th">
                    Created
                  </th>
                  <th scope="col" className="th sr-only">
                    Open
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {items.map((tenant) => (
                  <tr
                    key={tenant.id}
                    onClick={() => navigate(`/cp/tenants/${tenant.id}`)}
                    className="cursor-pointer transition-colors hover:bg-surface"
                  >
                    <td className="td">
                      <Link
                        to={`/cp/tenants/${tenant.id}`}
                        onClick={(event) => event.stopPropagation()}
                        className="rounded font-medium text-slate-900 hover:text-brand-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2"
                      >
                        {tenant.name}
                      </Link>
                      <p className="mt-0.5 text-xs text-slate-400">
                        {tenant.timezone} · {tenant.currency}
                      </p>
                    </td>
                    <td className="td">
                      <TenantStatusBadge status={tenant.status} />
                    </td>
                    <td className="td">
                      {/* "No plan" is a real, expected state, not a warning:
                          a tenant may legitimately be onboarded before a tier
                          is agreed. Styled as muted text rather than a badge so
                          it does not compete with the over-limit signal. */}
                      {tenant.planName ?? (
                        <span className="text-slate-400">No plan</span>
                      )}
                    </td>
                    <td className="td tabular-nums">{formatCount(tenant.branchCount)}</td>
                    <td className="td tabular-nums">
                      {tenant.deviceCount > 0 ? (
                        formatCount(tenant.deviceCount)
                      ) : (
                        <Badge tone="warning">No device</Badge>
                      )}
                    </td>
                    <td className="td whitespace-nowrap text-slate-500">{formatDate(tenant.createdAt)}</td>
                    <td className="td text-right">
                      <IconChevronRight className="ml-auto h-4 w-4 text-slate-300" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!isPending && !isError && items.length > 0 ? (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-4 py-3.5 sm:px-5">
            <p className="text-xs text-slate-500 tabular-nums">
              Showing {rangeStart}–{rangeEnd} of {formatCount(total)}
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                icon={<IconChevronLeft className="h-3.5 w-3.5" />}
                disabled={page <= 1 || isFetching}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                Previous
              </Button>
              <span className="px-1 text-xs text-slate-500 tabular-nums">
                Page {page} of {totalPages}
              </span>
              <Button
                size="sm"
                disabled={page >= totalPages || isFetching}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
                <IconChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}

function TableSkeleton() {
  return (
    <div className="divide-y divide-slate-100">
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="flex items-center gap-4 px-5 py-4">
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-3.5 w-40" />
            <Skeleton className="h-2.5 w-24" />
          </div>
          <Skeleton className="h-5 w-16 rounded-full" />
          <Skeleton className="hidden h-3.5 w-8 sm:block" />
          <Skeleton className="hidden h-3.5 w-8 sm:block" />
          <Skeleton className="hidden h-3.5 w-24 md:block" />
        </div>
      ))}
    </div>
  );
}
